import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiService, ParamList } from './api.service';

import { PendingFlow } from '../../flow/models/flow.model';
import { FlowIndex } from "../../common/models/flow.dto";
import { WorkOrderType, WorkOrderWithAssignments, WorkOrderStatus, WorkOrder, PendingWorkOrderAssignment, ScanOrder } from '../models/work-order.model';
import { WorkOrderSearchSpec } from '../../models/work-order-search-spec.model';
import { RequestForScanning } from "../../common/models/scan-documents.model";
import { WorkflowMetadata } from '../../applications/models/applications.models';


@Injectable()
export class WorkOrderService {

    static readonly BaseUrl = '/api/workorder';

    constructor(
        public http: HttpClient,
        public api: ApiService
    ) { }

    addWorkOrders(workItems: { workOrderId: string, workOrders: WorkOrder[] }): Observable<WorkOrder[]> {
        return this.http.post<WorkOrder[]>(`${WorkOrderService.BaseUrl}/addMany`, workItems);
    }

    assignWorkOrder(assignment: PendingWorkOrderAssignment): Observable<WorkOrder | FlowIndex> {
        return this.http.post<WorkOrder | FlowIndex>(`${WorkOrderService.BaseUrl}/assign`, assignment);
    }

    //if new endpoint in workordercontroller, add to here
    assignAndReindexMetaData(assignment: PendingWorkOrderAssignment): Observable<PendingFlow> { return this.http.post<PendingFlow>(`${WorkOrderService.BaseUrl}/assign-reindex-metadata`, assignment) }


    changeStatus(id: string, status: WorkOrderStatus) {
        return this.http.put<WorkOrder>(`${WorkOrderService.BaseUrl}/change-status/${id}/${status}`, undefined);
    }

    completeApplicationWorkOrders(applicationNumber: string): Observable<Array<WorkOrder>> {
        const uri = `${WorkOrderService.BaseUrl}/complete-application-workorders/${applicationNumber}`;
        let results = this.http.post<Array<WorkOrder>>(uri, null);
        return results;
    }

    getApplicationWorkOrders(applicationNumber: string, types: Array<WorkOrderType>, statuses: Array<WorkOrderStatus> = new Array<WorkOrderStatus>()): Observable<Array<WorkOrderWithAssignments>> {
        const uri = `${WorkOrderService.BaseUrl}/application-associated/${applicationNumber}` + this.api.buildParameterList([new ParamList('types', types), new ParamList('statuses', statuses)]);
        let results = this.http.get<Array<WorkOrderWithAssignments>>(uri);
        return results;
    }

    getOrganizationWorkOrders(orgId: string, types?: WorkOrderType[], statuses?: WorkOrderStatus[], templateIds?: string[]): Observable<Array<WorkOrderWithAssignments>> {
        let uri = `${WorkOrderService.BaseUrl}/organization/${orgId}`;
        let typesPart = new ParamList('types', types);
        let statusesPart = new ParamList('statuses', statuses);
        let templatesPart = new ParamList('templateIds', templateIds);
        uri += this.api.buildParameterList([typesPart, statusesPart, templatesPart]);
        return this.http.get<Array<WorkOrderWithAssignments>>(uri);
    }

    getPendingFlows(search: WorkOrderSearchSpec, filterForCurrentUser: boolean): Observable<PendingFlow[]> {
        const uri = `${WorkOrderService.BaseUrl}/pending-flows/${filterForCurrentUser}`;
        return this.http.post<PendingFlow[]>(uri, search);
    }

    getPendingFlowsByTargetTransition(workOrderTransitionSearchSpecName: string, buildOutWorkOrderAssignments: boolean = false): Observable<PendingFlow[]> {
        let url = `${WorkOrderService.BaseUrl}/transitionTargetSearch/pendingFlows/${workOrderTransitionSearchSpecName}/${buildOutWorkOrderAssignments}`;
        return this.http.get<PendingFlow[]>(url);
    }

    getUserWorkOrders(id: string, types: Array<WorkOrderType>, statuses: Array<WorkOrderStatus> = new Array<WorkOrderStatus>()): Observable<Array<WorkOrderWithAssignments>> {
        const uri = `${WorkOrderService.BaseUrl}/user-associated/${id}` + this.api.buildParameterList([new ParamList('types', types), new ParamList('statuses', statuses)]);
        let results = this.http.get<Array<WorkOrderWithAssignments>>(uri);
        return results;
    }

    getWorkOrder(id: string): Observable<WorkOrderWithAssignments> {
        return this.http.get<WorkOrderWithAssignments>(`${WorkOrderService.BaseUrl}/${id}`);
    }

    getWorkOrdersByTargetTransition(workOrderTargetTransitionSearchSpecName: string, buildOutWorkOrderAssignments: boolean = false): Observable<Array<WorkOrderWithAssignments>> {
        const uri = `${WorkOrderService.BaseUrl}/transitionTargetSearch/${workOrderTargetTransitionSearchSpecName}/${buildOutWorkOrderAssignments}`;
        return this.http.get<Array<WorkOrderWithAssignments>>(uri);
    }

    getWorkOrdersByStatusAndType(status: number, type: number): Observable<Array<WorkOrderWithAssignments>> {
        return this.http.get<Array<WorkOrderWithAssignments>>(`${WorkOrderService.BaseUrl}/${status}/${type}`);
    }

    save(workOrder: WorkOrder): Observable<WorkOrder> {
        return this.http.post<WorkOrder>(`${WorkOrderService.BaseUrl}/Save`, workOrder);
    }


    submitScanRequest(request: RequestForScanning): Observable<WorkOrder> {
        return this.http.post<WorkOrder>(`${WorkOrderService.BaseUrl}/submit-scan-request`, request);
    }

}
